import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { twJoin } from "tailwind-merge";

import type { Notification } from "@/api/bale.zod";

import {
  useGetEventSuspense,
  useGetProfileSuspense,
  useListNotificationsSuspenseInfinite,
  useUpdateNotification,
} from "@/api/bale";
import { Button } from "@/components/Button";
import { Card, CardSubtitleHeader } from "@/components/Card";
import { ContentFull, ContentLayout } from "@/components/ContentLayout";
import { FullScreenLayout } from "@/components/FullScreenLayout";
import { MenuItem } from "@/components/MenuItem";
import { Missing } from "@/components/Missing";
import { TextLoading } from "@/components/TextLoading";
import { profileDisplayName } from "@/model/profile";

export function NotificationsPage() {
  const { t } = useTranslation();
  const notifications = useListNotificationsSuspenseInfinite(
    { status: ["Read", "Unread"], limit: 20 },
    {
      query: {
        select: (data) => data.pages.flatMap((page) => page.data.notifications),
        getNextPageParam: (data) => data.data.page_info.next_cursor,
      },
    },
  );

  return (
    <FullScreenLayout>
      <ContentLayout wet title={<Link to="/notifications">{t("notifications-title")}</Link>}>
        <ContentFull className="flex flex-col gap-4 pt-28">
          <Card>
            <CardSubtitleHeader>{t("notifications-title")}</CardSubtitleHeader>
            <div className="flex flex-col">
              {notifications.data.map((notification) => (
                <Suspense
                  key={notification.id}
                  fallback={
                    <MenuItem disabled>
                      <TextLoading />
                    </MenuItem>
                  }
                >
                  <NotificationItem notification={notification} />
                </Suspense>
              ))}
              {notifications.data.length === 0 && <Missing className="mx-4 mb-4">{t("notifications-none")}</Missing>}
              {notifications.hasNextPage && (
                <Button
                  type="button"
                  disabled={notifications.isFetchingNextPage}
                  onClick={() => notifications.fetchNextPage()}
                >
                  {t("load-more")}
                </Button>
              )}
            </div>
          </Card>
        </ContentFull>
      </ContentLayout>
    </FullScreenLayout>
  );
}

function NotificationItem({ notification }: { notification: Notification }) {
  switch (notification.type) {
    case "EventInviteReceived":
      return <EventInviteReceived notification={notification} />;
    case "FriendRequestReceived":
      return <FriendRequestReceived notification={notification} />;
    case "FriendRequestAccepted":
      return <FriendRequestAccepted notification={notification} />;
    default:
      notification satisfies never;
      return null;
  }
}

function EventInviteReceived({
  notification,
}: {
  notification: Extract<Notification, { type: "EventInviteReceived" }>;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const event = useGetEventSuspense(notification.event_id, {}, { query: { select: (data) => data.data.event } });
  const inviter = useGetProfileSuspense(notification.account_id, { query: { select: (data) => data.data.profile } });
  const update = useUpdateNotification({ mutation: { onSuccess: () => navigate(`/event/${notification.event_id}`) } });

  function acknowledge() {
    update.mutate({ notificationId: notification.id, data: { is_acknowledged: true } });
  }

  return (
    <MenuItem onClick={acknowledge}>
      <div className={twJoin("size-2 rounded-full", notification.acknowledged_at ? "bg-black/10" : "bg-turtle")} />
      <p className={notification.acknowledged_at ? "font-disinterest" : "font-interest"}>
        {t("notify-event-invite-received", { inviter: profileDisplayName(inviter.data), event: event.data.name })}
      </p>
      <p className="ml-auto font-hint text-placeholder">{t("datetime", { date: notification.created_at })}</p>
    </MenuItem>
  );
}

function FriendRequestReceived({
  notification,
}: {
  notification: Extract<Notification, { type: "FriendRequestReceived" }>;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const profile = useGetProfileSuspense(notification.account_id, { query: { select: (data) => data.data.profile } });
  const update = useUpdateNotification({
    mutation: { onSuccess: () => navigate(`/profile/${notification.account_id}`) },
  });

  function acknowledge() {
    update.mutate({ notificationId: notification.id, data: { is_acknowledged: true } });
  }

  return (
    <MenuItem onClick={acknowledge}>
      <div className={twJoin("size-2 rounded-full", notification.acknowledged_at ? "bg-black/10" : "bg-turtle")} />
      <p className={notification.acknowledged_at ? "font-disinterest" : "font-interest"}>
        {t("notify-friend-request-received", { name: profileDisplayName(profile.data) })}
      </p>
      <p className="ml-auto font-hint text-placeholder">{t("datetime", { date: notification.created_at })}</p>
    </MenuItem>
  );
}

function FriendRequestAccepted({
  notification,
}: {
  notification: Extract<Notification, { type: "FriendRequestAccepted" }>;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const profile = useGetProfileSuspense(notification.account_id, { query: { select: (data) => data.data.profile } });
  const update = useUpdateNotification({
    mutation: { onSuccess: () => navigate(`/profile/${notification.account_id}`) },
  });

  function acknowledge() {
    update.mutate({ notificationId: notification.id, data: { is_acknowledged: true } });
  }

  return (
    <MenuItem onClick={acknowledge}>
      <div className={twJoin("size-2 rounded-full", notification.acknowledged_at ? "bg-black/10" : "bg-turtle")} />
      <p className={notification.acknowledged_at ? "font-disinterest" : "font-interest"}>
        {t("notify-friend-request-accepted", { name: profileDisplayName(profile.data) })}
      </p>
      <p className="ml-auto font-hint text-placeholder">{t("datetime", { date: notification.created_at })}</p>
    </MenuItem>
  );
}
